import React from 'react';
import { MapPin, Check } from 'lucide-react';

const AddressCard = ({
  address,
  isActive = false,
  colorScheme = 'blue',
  onSelect,
  onEdit,
  onDelete
}) => {
  const colorSchemes = {
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      activeBorder: 'border-blue-500',
      icon: 'text-blue-600',
      activeBg: 'bg-blue-100'
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      activeBorder: 'border-purple-500',
      icon: 'text-purple-600',
      activeBg: 'bg-purple-100'
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      activeBorder: 'border-green-500',
      icon: 'text-green-600',
      activeBg: 'bg-green-100'
    },
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      activeBorder: 'border-orange-500',
      icon: 'text-orange-600',
      activeBg: 'bg-orange-100'
    }
  };

  const scheme = colorSchemes[colorScheme] || colorSchemes.blue;

  return (
    <div
      className={`relative p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer hover:shadow-md ${
        isActive
          ? `${scheme.activeBg} ${scheme.activeBorder} shadow-md`
          : `${scheme.bg} ${scheme.border} hover:${scheme.activeBorder}`
      }`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      aria-label={`Address: ${address.street}, ${address.city}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      {/* Active indicator */}
      {isActive && (
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
          <Check className="w-4 h-4 text-white" aria-hidden="true" />
        </div>
      )}

      {/* Address type badge */}
      <div className="flex items-center justify-between mb-3">
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${scheme.icon} ${scheme.bg} border ${scheme.border}`}>
          <MapPin className="w-3 h-3 mr-1" aria-hidden="true" />
          {address.type || 'Address'}
        </span>

        {/* Action buttons */}
        <div className="flex gap-1">
          <button 
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(address);
            }}
            className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-white hover:bg-opacity-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50"
            aria-label="Edit address"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(address.id);
            }}
            className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-white hover:bg-opacity-50 transition-colors focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-50"
            aria-label="Delete address"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Address details */}
      <div className="space-y-1 break-words">
        <h4 className="font-medium text-gray-900 text-sm">
          {address.address}
        </h4>
        <p className="text-sm text-gray-600 ">
          {address.street}
        </p>
        <p className="text-sm text-gray-600">
          {address.city}, {address.state} {address.zipCode}
        </p>
        {address.country && (
          <p className="text-sm text-gray-600">
            {address.country}
          </p>
        )}
        {address.phone && (
          <p className="text-xs text-gray-500 mt-2">
            Phone: {address.phone}
          </p>
        )}
      </div>
    </div>
  );
};

export default AddressCard;
